import { CalendarTypes } from 'src/components'
import { addNumberSuffix, plural } from './util'

export const getNewCompletedTaskDate = ({ complete, start, end, repeat, wholeDay }) => {
    const nextValidDate = getNextValidDate(repeat, start)
    const nextStart = nextValidDate || start
    const nextComplete = !nextValidDate
    return repeat
        ? {
              complete: nextComplete,
              start: nextStart,
              end: null,
              repeat: { ...repeat, repititions: reduceRepititions(repeat.repititions) },
              wholeDay,
          }
        : {
              complete,
              start,
              end,
              repeat,
              wholeDay,
          }
}

export function getNextValidDate(repeat, startDay = new Date()) {
    if (!repeat) return null
    if (!repeat.frequency) return null

    const { interval, weekday, frequency, from, to, repetitions } = repeat
    const today = startDay ? new Date(startDay) : new Date()
    let startDate = new Date(Math.max(today.getTime(), new Date(from).getTime())) // Start from today or `from` date

    // If `to` is defined and the starting date is after it, return null
    if (to && startDate > new Date(to)) return null

    // If repetitions are defined and exhausted, return null
    if (repetitions !== undefined && repetitions !== null && repetitions <= 0) return null

    let nextDate = new Date(startDate)

    switch (frequency) {
        case 'day':
            nextDate.setDate(nextDate.getDate() + interval)
            break

        case 'week':
            nextDate.setDate(nextDate.getDate() + interval * 7)
            break

        case 'weekday':
            let daysAdded = 0
            while (daysAdded < interval) {
                nextDate.setDate(nextDate.getDate() + 1)
                if (weekday.includes(nextDate.getDay())) {
                    daysAdded++
                }
            }
            break

        case 'month':
            nextDate.setMonth(nextDate.getMonth() + interval)
            break

        case 'monthday':
            const targetDay = interval // The day of the month to target
            do {
                nextDate.setMonth(nextDate.getMonth() + 1) // Move to the next month
                const daysInNextMonth = daysInMonth(nextDate)
                if (targetDay <= daysInNextMonth) {
                    nextDate.setDate(targetDay) // Set to the specified day if valid
                    break
                }
            } while (true)
            break

        case 'monthweek':
            const currentWeekDay = nextDate.getDay()
            nextDate.setDate(nextDate.getDate() + (interval * 7 - (currentWeekDay % 7)))
            break

        case 'year':
            nextDate.setFullYear(nextDate.getFullYear() + interval)
            break

        default:
            throw new Error('Unsupported frequency type')
    }

    // If `to` is defined and the calculated date exceeds it, return null
    if (to && nextDate > new Date(to)) return null

    // Decrement repetitions if defined
    if (repetitions !== undefined && repetitions !== null) repeat.repetitions--

    return nextDate
}

function daysInMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
}

export const reduceRepititions = (repetitions) => {
    if (repetitions == null || repetitions == undefined) return null
    if (repetitions >= 0) return null

    return repetitions - 1
}

export const getRepeatFrequencyText = (repeat: CalendarTypes.Repeat, weekdays: string[]) => {
    const { interval = 1, weekday = [], frequency = 'day', to, repetitions = 0 } = repeat
    const from = repeat.from ? new Date(repeat.from) : new Date()

    let label = ''

    if (frequency == 'day' || frequency == 'week' || frequency == 'month' || frequency == 'year') {
        if (interval == 1) {
            label = 'Every ' + frequency
        } else {
            label = 'Every ' + addNumberSuffix(interval) + ' ' + frequency
        }
    }

    if (frequency == 'weekday') {
        label = 'Every ' + weekday.map((i) => weekdays[i]).join(', ')
    }

    if (frequency == 'monthday') {
        label = 'Every ' + addNumberSuffix(interval) + ' day of the month'
    }

    if (frequency == 'monthweek') {
        label = 'Every ' + addNumberSuffix(interval) + ' week of the month'
    }

    label +=
        ' from ' +
        from.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        })

    if (to && !repetitions) {
        label +=
            ' until ' +
            to.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
            })
    }

    if (repetitions && !to) {
        label += ' for ' + repetitions + plural(repetitions, ' time')
    }

    return label
}
